import type { GetServerSideProps } from 'next';

import { withFixedPageCommonProps } from '@/utils/fixedPage';
import { getLocaleFromParams } from '@/utils/locale';

import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { ContactHit } from '@/integrations/algolia/types';
import { fetchFixedPageData } from '@/integrations/contentful';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import { resolveContentDraftMode } from '@/lib/draftMode';
import { allowedCountries } from '@/lib/eccomerceCountries';
import { captureError } from '@/lib/errors';
import { paths } from '@/lib/paths';
import { getGqlRegionFromLocale, getRegionFromLocale } from '@/lib/regions';
import { CacheHeaderType } from '@/lib/revalidate';
import { getContactPathWithLocale } from '@/lib/service/helpers';
import { ServiceContactView } from '@/views/ServiceContactView/ServiceContactView';

export const getServerSideProps: GetServerSideProps = commonServerSideProps(
  async (context) => {
    const draftMode = resolveContentDraftMode(context);
    const routerLocale = getLocaleFromParams(context?.params);
    const { country, locale } = getRegionFromLocale(routerLocale);
    const { channel, languageCode } = getGqlRegionFromLocale(routerLocale);

    if (!allowedCountries?.includes(country.code)) {
      return {
        redirect: {
          destination: getContactPathWithLocale(routerLocale),
          permanent: false,
        },
      };
    }
    const algoliaSsrSearchClient = createSearchClient({
      isPreview: draftMode,
      isSsr: true,
    });

    const index = algoliaSsrSearchClient.initIndex(
      `${process.env.NEXT_PUBLIC_ENVIRONMENT_NAME}.CONTACT_CENTER`,
    );

    try {
      const { hits } = await index.search<ContactHit>('', {
        filters: `country_code:${country.code}`,
        attributesToRetrieve: ['phone_number', 'email', 'hours'],
      });

      if (!hits.length || !hits[0].email || !hits[0].phone_number) {
        return {
          redirect: {
            destination: paths.contact.asPath(),
            permanent: false,
          },
        };
      }
      const contactInfo = hits[0];

      const contentfulLocale = locale.contentful ?? locale.locale;
      const { cms, seoConfigBuilder } = await fetchFixedPageData(
        'service/contact',
        contentfulLocale,
        languageCode,
        channel,
        country.slug,
        draftMode,
      );

      if (!cms?.topContent || !cms?.bottomContent) {
        return {
          redirect: {
            destination: paths.contact.asPath(),
            permanent: false,
          },
        };
      }

      return {
        props: {
          cms,
          contactInfo,
          country,
          seoConfig: seoConfigBuilder?.build(),
        },
      };
    } catch (error) {
      captureError(error as Error, {
        extra: {
          error,
          context,
        },
      });
      return {
        redirect: {
          destination: getContactPathWithLocale(routerLocale),
          permanent: false,
        },
      };
    }
  },
  CacheHeaderType.NO_CACHE,
);
export default withFixedPageCommonProps(ServiceContactView);
